import { useMemo } from 'react';

import SubActivityView from '../sub-activity/sub-activity-view';
import { ViewProps } from './discussion.constants';
import { ZoomComponentTrigger } from 'approot/activity-builder/activity-builder-zoom-component-modal';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { ComponentExpandModeType } from 'approot/activity-builder/present/activity-builder-present-data-tracking.utils';
import {
  EVENT_ACTION_EDIT,
  EVENT_ACTION_VIEW,
} from 'approot/shared/data-tracker/data-events.constants';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import { useTeacherGetAnswers } from 'approot/activity-builder/present/activity-answers.hooks';
import { DiscussionViewWrapper } from './discussion-view.style';
import DiscussionViewQuestion from './discussion-view-question';
import DiscussionAnswerTeacher from './discussion-answer-teacher';
import { discussionHasAnswer } from './logic/discussion-has-answer';
import ComponentLoading from '../shared/component-loading';

const DiscussionView = (
  props: ViewProps & {
    onComponentExpand?: (expandEventType: ComponentExpandModeType) => void;
  }
) => {
  const { id, type, properties, subActivityProperties, onComponentExpand } =
    props;
  const { style } = properties;

  const canRespond = false;
  const { isPublished, isPublic } = useActivityBuilderPresentContext();
  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const canAnswer = !isPublic && isPublished && canRespond;

  const { componentAnswers, hasFetchedAnswers, error } = useTeacherGetAnswers({
    componentId: id,
    skip: !canRespond,
  });

  const hasAnswerData = useMemo(
    () => discussionHasAnswer(componentAnswers || []),
    [componentAnswers]
  );

  return (
    <SubActivityView subActivityDetails={subActivityProperties}>
      {isVisible && (
        <DiscussionAnswerTeacher
          {...props}
          isPublished={isPublished}
          isPublic={isPublic}
          canRespond={canRespond}
          closeModal={closeModal}
        />
      )}
      {!error && (
        <ZoomComponentTrigger
          openModal={openModal}
          canAddResponse={canRespond}
          hoverText={canRespond ? 'Expand and Answer' : 'Expand'}
          hideBorder
          onComponentExpand={() =>
            onComponentExpand?.(
              canRespond ? EVENT_ACTION_EDIT : EVENT_ACTION_VIEW
            )
          }
        />
      )}
      <DiscussionViewWrapper>
        <DiscussionViewQuestion
          id={id}
          type={type}
          discussion={properties.discussion}
          style={style}
          hasAnswer={hasAnswerData}
          componentLoading={
            canAnswer && (
              <ComponentLoading hasData={hasFetchedAnswers} error={error} />
            )
          }
          onClick={() => {
            onComponentExpand?.(
              canRespond ? EVENT_ACTION_EDIT : EVENT_ACTION_VIEW
            );
            openModal();
          }}
        />
      </DiscussionViewWrapper>
    </SubActivityView>
  );
};

export default DiscussionView;
